import { useCallback, useEffect, useReducer, useRef, useState } from 'react';
import { api, type RenderFragment, type RenderInfo } from './api';
import { mergeSegmentFragment } from './fragmentState';
import { mergeRenderFragmentUpdates, type FragmentUpdate, initialRenderLoadState, matchesRenderScope, renderLoadReducer, type LoadedRender, type RenderScope } from './projectRenderState';

export function projectRenderScope(workspace: string): RenderScope | undefined {
  if (workspace === 'segments') return 'fragments';
  if (workspace === 'delivery' || workspace === 'scenes') return 'delivery';
  return undefined;
}

export function useProjectRender(projectId: string | undefined, workspace: string) {
  const [render, setRender] = useState<RenderInfo>({ available: false });
  const [loadState, dispatchLoad] = useReducer(renderLoadReducer, initialRenderLoadState);
  const loadedRef = useRef<LoadedRender | undefined>(undefined);
  const [historicalProjectId, setHistoricalProjectId] = useState<string>();
  const [revision, setRevision] = useState(0);
  const sequence = useRef(0);
  const fragmentRevision = useRef(0);
  const fragmentUpdates = useRef(new Map<number, FragmentUpdate>());
  const mergeFragment = useCallback((fragment: RenderFragment) => {
    fragmentUpdates.current.set(fragment.order, { revision: ++fragmentRevision.current, fragment });
    setRender(current => mergeSegmentFragment(current, fragment));
  }, []);
  const scope = projectRenderScope(workspace);
  const historical = workspace === 'delivery' && Boolean(projectId && historicalProjectId === projectId);
  const invalidate = useCallback(() => {
    sequence.current += 1;
    loadedRef.current = undefined;
    dispatchLoad({ type: 'refresh' });
    setHistoricalProjectId(undefined);
    setRevision(value => value + 1);
  }, []);

  const viewHistorical = useCallback(() => {
    sequence.current += 1;
    loadedRef.current = undefined;
    dispatchLoad({ type: 'reset' });
    setHistoricalProjectId(projectId);
    setRevision(value => value + 1);
  }, [projectId]);

  useEffect(() => {
    loadedRef.current = undefined;
    dispatchLoad({ type: 'reset' });
    setRender({ available: false });
    fragmentUpdates.current.clear();
    fragmentRevision.current = 0;
    setHistoricalProjectId(undefined);
  }, [projectId]);

  useEffect(() => {
    if (!projectId || !scope) return;
    const cached = loadedRef.current;
    if (matchesRenderScope(cached, projectId, scope, historical)) return;
    dispatchLoad({ type: 'refresh' });
    const current = ++sequence.current;
    const requestRevision = fragmentRevision.current;
    const controller = new AbortController();
    const load = scope === 'delivery' ? (historical ? api.latestRender : api.currentRender) : api.projectFragments;
    void load(projectId, controller.signal).then(data => {
      if (controller.signal.aborted || sequence.current !== current) return;
      setRender(mergeRenderFragmentUpdates(data, fragmentUpdates.current.values(), requestRevision));
      const next = { projectId, scope, historical, hasFragments: Array.isArray(data.fragments) };
      loadedRef.current = next;
      dispatchLoad({ type: 'loaded', value: next });
    }).catch((reason: Error) => {
      if (!controller.signal.aborted && sequence.current === current) dispatchLoad({ type: 'failed', error: reason.message });
    });
    return () => controller.abort();
  }, [projectId, scope, historical, revision]);

  const ready = !scope || matchesRenderScope(loadState.visible, projectId, scope, historical);
  return { render, setRender, mergeFragment, ready, error: loadState.error, refreshing: Boolean(scope && loadState.refreshing), invalidate, viewHistorical, scope };
}
